import {useState} from "react";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button.tsx";
import {XMarkIcon} from "@heroicons/react/24/outline";
import {toast} from "sonner";
import {LearningUserDetail} from "@/features/user-directory/types";
import {ChipSearchResult} from "@/types/chipAssignment";
import {useUnassignChipMutation} from "@/store/chipAssignmentApi.ts";
import {ChipSearchBar} from "./ChipSearchBar";
import {UnassignConfirmDialog} from "./UnassignConfirmDialog";

interface UserResultCardProps {
    user: LearningUserDetail;
    chips: ChipSearchResult[];
    isLoadingChips?: boolean;
    onSelectChipToAssign: (chip: ChipSearchResult) => void;
    onCreateChip: () => void;
    disabled?: boolean;
}

// הצגת פרטי learning.User + רשימת Chips משויכים + חיפוש/בחירת Chip לשיוך.
export function UserResultCard({
    user,
    chips,
    isLoadingChips,
    onSelectChipToAssign,
    onCreateChip,
    disabled,
}: UserResultCardProps) {
    const [unassignChip, {isLoading: isUnassigning}] = useUnassignChipMutation();
    const [chipToUnassign, setChipToUnassign] = useState<ChipSearchResult | null>(null);

    async function handleConfirmUnassign() {
        if (!chipToUnassign) return;
        try {
            await unassignChip({chip_id: chipToUnassign.id, user_id: user.id}).unwrap();
            toast.success("שיוך הכרטיס נותק בהצלחה");
            setChipToUnassign(null);
        } catch (err: unknown) {
            const msg = (err as {data?: {msg?: string}})?.data?.msg || "שגיאה בניתוק השיוך";
            toast.error(msg);
        }
    }

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle className="text-lg">{user.name || 'ללא שם'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-base">
                    <div><span className="text-muted-foreground">ת.ז.: </span>{user.tz || '—'}</div>
                    <div><span className="text-muted-foreground">טלפונים: </span>{user.phones.join(', ') || '—'}</div>
                    <div><span className="text-muted-foreground">כתובת: </span>{user.address || '—'}</div>
                    <div><span className="text-muted-foreground">עיר: </span>{user.city || '—'}</div>
                </div>

                <div className="space-y-2">
                    <div className="text-base font-medium">כרטיסים משויכים</div>
                    {isLoadingChips ? (
                        <p className="text-sm text-muted-foreground">טוען...</p>
                    ) : chips.length === 0 ? (
                        <p className="text-sm text-muted-foreground">אין כרטיסים משויכים למשתמש זה</p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {chips.map((chip) => (
                                <Badge key={chip.id} variant="secondary" className="text-sm gap-1 pe-1">
                                    קוד פנימי: {chip.internal_code}
                                    {chip.external_number ? ` · קוד חיצוני: ${chip.external_number}` : ''}
                                    <button
                                        type="button"
                                        title="נתק שיוך"
                                        onClick={() => setChipToUnassign(chip)}
                                        disabled={disabled}
                                        className="rounded-full hover:bg-black/10 p-0.5 disabled:opacity-50"
                                    >
                                        <XMarkIcon className="h-3 w-3" />
                                    </button>
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>

                <div className="space-y-2">
                    <div className="text-base font-medium">שיוך כרטיס למשתמש זה</div>
                    <ChipSearchBar onSelect={onSelectChipToAssign} disabled={disabled} />
                    <Button type="button" variant="outline" className="text-base" onClick={onCreateChip} disabled={disabled}>
                        + צור כרטיס חדש ושייך
                    </Button>
                </div>

                <UnassignConfirmDialog
                    open={Boolean(chipToUnassign)}
                    chipLabel={chipToUnassign?.internal_code ?? null}
                    chipExternalCode={chipToUnassign?.external_number ?? null}
                    isLoading={isUnassigning}
                    onCancel={() => setChipToUnassign(null)}
                    onConfirm={handleConfirmUnassign}
                />
            </CardContent>
        </Card>
    );
}

